import { OverflowList } from '@theme/overflow-list';
import VariantPicker from '@theme/variant-picker';
import { ProductComponent } from '@theme/view-event-elements';
import { debounce, isDesktopBreakpoint, mediaQueryLarge, yieldToMainThread } from '@theme/utilities';
import { SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Returns the deepest element the event was originally dispatched on.
 *
 * The theme's delegated `on:click` handlers receive a proxied event whose `target` is the element
 * that declares the handler (e.g. the <product-card> container), so the real target has to be read
 * from the composed path to tell whether a click landed on an interactive descendant.
 *
 * @param {Event} event
 * @returns {Element | null}
 */
function getEventTarget(event) {
  const target = event.composedPath?.()[0] ?? event.target;
  return target instanceof Element ? target : null;
}

/**
 * @typedef {object} ProductCardLinkRefs
 * @property {HTMLElement} [cardGallery] - The card gallery element.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 */

/**
 * A custom element for product links with images for transitions to PDP.
 * This is a base class that is extended by ProductCard.
 * Used directly by resource-card.liquid for non-product-card scenarios.
 * Extends ProductComponent to automatically emit product:view events when visible.
 *
 * @template {ProductCardLinkRefs} [T=ProductCardLinkRefs]
 * @extends {ProductComponent<T>}
 */
export class ProductCardLink extends ProductComponent {
  get productTransitionEnabled() {
    return this.getAttribute('data-product-transition') === 'true';
  }

  get featuredMediaUrl() {
    return this.getAttribute('data-featured-media-url');
  }

  /**
   * Handles the click event for view transitions.
   * @param {Event} event
   */
  handleViewTransition(event) {
    // If the event has been prevented, don't do anything, another component is handling the click
    if (event.defaultPrevented) return;

    // If the event was on an interactive element, don't do anything, this is not a navigation
    const target = getEventTarget(event);
    if (target) {
      const interactiveElement = target.closest('button, input, label, select, [tabindex="1"], .product-variant');
      if (interactiveElement) return;
    }

    if (!this.productTransitionEnabled) return;

    const { cardGallery } = this.refs;
    if (!cardGallery || !cardGallery.hasAttribute('data-view-transition-to-main-product')) return;

    // Check on the current active image, whether it's a product card image or a resource card image
    const { imagesToTransition } = this.refs;
    const activeImage =
      imagesToTransition?.find(
        (/** @type {HTMLImageElement} */ image) =>
          image.closest('slideshow-slide')?.getAttribute('aria-hidden') === 'false',
      ) || imagesToTransition?.[imagesToTransition.length - 1];

    if (activeImage instanceof HTMLImageElement) this.#setImageSrcset(activeImage);

    cardGallery.setAttribute('data-view-transition-type', 'product-image-transition');
    cardGallery.setAttribute('data-view-transition-triggered', 'true');
  }

  /**
   * Sets the srcset for the image
   * @param {HTMLImageElement} image
   */
  #setImageSrcset(image) {
    if (!this.featuredMediaUrl) return;

    const currentImageUrl = new URL(image.currentSrc);

    // Deliberately not using origin, as it includes the protocol, which is usually skipped for featured media
    const currentImageRawUrl = currentImageUrl.host + currentImageUrl.pathname;

    if (!this.featuredMediaUrl.includes(currentImageRawUrl)) {
      const imageFade = image.animate([{ opacity: 0.8 }, { opacity: 1 }], {
        duration: 125,
        easing: 'ease-in-out',
      });

      imageFade.onfinish = () => {
        image.srcset = this.featuredMediaUrl ?? '';
      };
    }
  }
}

if (!customElements.get('product-card-link')) {
  customElements.define('product-card-link', ProductCardLink);
}

/**
 * A custom element that displays a product card.
 * Extends ProductCardLink to inherit view transition functionality.
 *
 * @typedef {object} ProductCardRefs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement[]} [imagesToTransition] - The images to transition.
 * @extends {ProductCardLink<ProductCardRefs>}
 */
export class ProductCard extends ProductCardLink {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  #fetchProductPageHandler = () => {
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();
    this.#hideDuplicateText();

    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    // Card clicks arrive through the delegated `on:click="/handleCardClick"` declared on the
    // <product-card> element (see snippets/product-card.liquid), and variant toggles through their
    // own `on:click`/`on:keydown` (see snippets/product-variant-card.liquid). Binding those
    // handlers directly to this non-interactive container would make assistive technologies
    // expose it as an extra "clickable" control alongside the product link.

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    this.#preloadTimeout = setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  /**
   * Handles a click anywhere on the product card.
   *
   * Invoked through the delegated `on:click` attribute on the <product-card> element so the
   * non-interactive container itself has no click listener. Resolves the view transition first and
   * then delegates navigation to the single card link, which ignores clicks on interactive
   * descendants such as the variant toggles and quick-add.
   *
   * @param {Event} event
   */
  handleCardClick = (event) => {
    this.handleViewTransition(event);
    this.navigateToProduct(event);
  };

  /**
   * Hides the visible product text from assistive technologies.
   *
   * The card link already exposes the product name (brand, model and price) through its
   * visually-hidden label, so the visible title, heading and price would otherwise be announced a
   * second time. Interactive controls (variant toggles, swatches, quick-add) are left exposed.
   */
  #hideDuplicateText() {
    this.querySelectorAll('[data-swap-title-kind], product-price').forEach((element) => {
      element.setAttribute('aria-hidden', 'true');
    });
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the product select event (variant selected and updated).
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Update variant picker when variant:selected event fires
    const { optionValueId } = event.detail ?? {};
    if (optionValueId && event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(optionValueId);
    }

    // Wait for variant:update data via promise
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;

        // Update price, availability, and URL based on new variant
        this.updatePrice(html);
        this.#isUnavailableVariantSelected(html);
        this.#updateProductUrl(html);
        this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

        if (event.target !== this.variantPicker) {
          this.variantPicker?.updateVariantPicker(html);
        }

        this.#updateVariantImages();
        this.#previousSlideIndex = null;

        // Remove attribute after re-rendering since a variant selection has been made
        this.removeAttribute('data-no-swatch-selected');

        // Force overflow list to reflow after variant update
        this.#updateOverflowList();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    // Find the overflow list in the variant picker
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!overflowList || !isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        }),
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  updatePrice(html) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #updateProductUrl(html) {
    const responseProductCard = html.querySelector('product-card');
    const anchorElement = responseProductCard?.querySelector('a');
    const featuredMediaUrl = responseProductCard?.getAttribute('data-featured-media-url');

    // Update the featured media URL for view transitions (inherited from ProductCardLink)
    if (featuredMediaUrl) {
      this.setAttribute('data-featured-media-url', featuredMediaUrl);
    }

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {Document} html - The parsed HTML document with updated variant data.
   */
  #isUnavailableVariantSelected(html) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (html.querySelectorAll('input:checked'));

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    // The variant picker belongs to the original product, so its media ids do not match the
    // slides of a swapped in product.
    if (this.#swapped) return;

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }

      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Id of the pending hover preload timeout, tracked so it can be cancelled on swap.
   * @type {number | undefined}
   */
  #preloadTimeout;

  /**
   * Handle of the product currently rendered in the card. Initialized from the original
   * product handle on the first swap and updated on every swap.
   * @type {string | null}
   */
  #currentHandle = null;

  /**
   * Whether the card currently renders a product other than its original one.
   * @type {boolean}
   */
  #swapped = false;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      this.#preloadTimeout = setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;

    const { slideshow } = this.refs;

    if (!this.variantPicker) {
      if (!slideshow) return;
      slideshow.previous(undefined, { animate: false });
    } else {
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    // A swapped card only renders the new product's default and hover image, so there is no
    // variant image to fall back to.
    if (this.#swapped) {
      slideshow.select(0, undefined, { animate: false });
      return;
    }

    // If we have a selected variant, always use its image
    if (this.variantPicker?.selectedOption) {
      const id = this.variantPicker.selectedOption.dataset.optionMediaId;
      if (id) {
        slideshow.select({ id }, undefined, { animate: false });
        return;
      }
    }

    // No variant selected - use initial slide if it's valid
    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');
    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    // No valid initial slide or selected variant - go to previous
    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    const target = getEventTarget(event);
    if (!target) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = target.closest('button, input, label, select, [tabindex="1"], .product-variant');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const infiniteResultsList = this.closest('results-list[infinite-scroll="true"]');
    if (!window.Shopify.designMode && infiniteResultsList) {
      const url = new URL(window.location.href);
      const parent = this.closest('li');
      url.hash = productCardAnchor;
      if (parent && parent.dataset.page) {
        url.searchParams.set('page', parent.dataset.page);
      }

      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Handles click on a product variant card for inline product swap.
   * Uses pre-rendered templates from product-card-swap.liquid instead of API fetch, so the swap
   * itself is synchronous markup cloning and morphing and no loading state is needed.
   *
   * data-swap contract between the swap templates and the live card DOM:
   *
   * Template side (inside <template data-variant-handle="...">):
   * - [data-swap="title-inner"]        – <p> title or heading, keyed by data-swap-title-kind,
   *                                      mirrors blocks/product-title.liquid and
   *                                      blocks/product-heading.liquid
   * - [data-swap="price-inner"]        – output of snippets/price.liquid
   *                                      ([ref="priceContainer"] + optional [ref="volumePricingNote"])
   * - [data-swap="badges-inner"]       – output of snippets/product-card-badge.liquid
   * - [data-swap="slides"]             – rendered <slideshow-slide> markup for the product media,
   *                                      at most 2 slides. Produced by
   *                                      snippets/product-card-swap-media.liquid so that the swapped
   *                                      images keep the responsive srcset/sizes of first paint.
   * - <data data-swap="url">           – product URL applied to all card anchors
   * - <data data-swap="product-title"> – product title applied to quick-add metadata
   * - <data data-swap="available">     – "true"/"false" applied to quick-add buttons
   * - <data data-swap="product-id">    – product id applied to the card gallery
   * - <data data-swap="aspect-ratio">  – featured media aspect ratio, applied when image_ratio is adapt
   *
   * The swap container itself carries data-swap-original-handle, which identifies the product the
   * card initially renders. Clicking that card again falls through to normal navigation.
   *
   * Live DOM side:
   * - titles → [data-swap-title-kind] .text-block, one per title block
   *            (blocks/product-title.liquid, blocks/product-heading.liquid)
   * - price  → product-price [ref="priceContainer"] (blocks/price.liquid)
   * - badges → .product-badges[data-swap="badges"] (blocks/_product-card-gallery.liquid)
   * - urls   → refs productCardLink
   * - label  → [data-swap="link-label"] (snippets/product-card.liquid)
   * - media  → slideshow slides, replaced wholesale from the template markup
   *
   * To add a new swappable element: render it with a data-swap key in the template,
   * mark the live element, then morph it here.
   *
   * @param {Event} event
   */
  handleVariantCardClick = async (event) => {
    if (!(event.target instanceof Element)) return;

    const variantCard = event.target.closest('.product-variant');
    if (!(variantCard instanceof HTMLElement)) return;

    const handle = variantCard.dataset.variantHandle;
    if (!handle) return;

    // Find the pre-rendered template for this variant
    const swapContainer = this.querySelector('[data-product-card-swap]');
    const template = swapContainer?.querySelector(`template[data-variant-handle="${handle}"]`);
    if (!(template instanceof HTMLTemplateElement)) {
      console.warn('[product-card] No swap template found for handle:', handle);
      return;
    }

    // Don't swap if the card already renders this product. The click is left to the anchor,
    // so selecting the current product still navigates to it.
    const originalHandle = swapContainer?.getAttribute('data-swap-original-handle') ?? null;
    this.#currentHandle ??= originalHandle;
    if (handle === this.#currentHandle) return;

    event.preventDefault();
    event.stopPropagation();

    // The media is about to be replaced, so any pending hover preload is stale.
    clearTimeout(this.#preloadTimeout);

    try {
      const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));

      // Swap price content using morph (also syncs the volume pricing note)
      this.#swapPrice(fragment);

      // Swap badges inner content using morph
      this.#morphSwapContent(fragment, '[data-swap="badges-inner"]', '.product-badges[data-swap="badges"]');

      // Swap title inner content
      this.#swapTitleContent(fragment);

      // Swap the card link accessible name
      this.#swapLinkLabel(fragment);

      // Swap media (slideshow images)
      await this.#swapMedia(fragment);

      // Update all URLs
      this.#swapUrls(fragment);

      // Update quick-add metadata
      this.#swapQuickAddMeta(fragment);

      // Keep the gallery shell metadata in sync with the swapped product
      this.#syncGalleryShell(fragment);

      // Track which product the card currently renders
      this.#currentHandle = handle;
      this.#swapped = handle !== originalHandle;

      // Update variant cards selection
      this.#updateVariantSelection(variantCard);
    } catch (error) {
      console.warn('[product-card] Variant swap failed:', error);
    }
  };

  /**
   * Activates a variant card with the Space key.
   *
   * The cards are exposed as toggle buttons (`role="button"`), so they must respond to Space
   * in addition to the native Enter activation of the underlying anchor.
   *
   * @param {KeyboardEvent} event
   */
  handleVariantCardKeydown = (event) => {
    if (event.key !== ' ' && event.key !== 'Spacebar') return;
    if (!(event.target instanceof Element)) return;

    const variantCard = event.target.closest('.product-variant');
    if (!(variantCard instanceof HTMLElement)) return;

    // Prevent the page from scrolling, then reuse the click flow for the swap/navigation.
    event.preventDefault();
    variantCard.click();
  };

  /**
   * Swaps the price from the template into the live DOM.
   * The template's [data-swap="price-inner"] contains the output of snippets/price.liquid,
   * so the morphed markup always matches a server-side re-render of the price block.
   * @param {DocumentFragment} fragment - The template content.
   */
  #swapPrice(fragment) {
    const source = fragment.querySelector('[data-swap="price-inner"] [ref="priceContainer"]');
    const target = this.querySelector('product-price [ref="priceContainer"]');

    if (source && target) {
      morph(target, source);
    }

    // price.liquid renders the volume pricing note as a sibling of the price container:
    // sync it so it appears/disappears when swapping between volume and non-volume products.
    const sourceNote = fragment.querySelector('[data-swap="price-inner"] [ref="volumePricingNote"]');
    const targetNote = this.querySelector('product-price [ref="volumePricingNote"]');

    if (sourceNote && targetNote) {
      morph(targetNote, sourceNote);
    } else if (sourceNote && target) {
      target.insertAdjacentElement('afterend', sourceNote);
    } else if (targetNote) {
      targetNote.remove();
    }
  }

  /**
   * Morphs swap content from a template fragment into a live DOM target.
   * @param {DocumentFragment} fragment - The template content.
   * @param {string} sourceSelector - The selector to find in the template fragment.
   * @param {string} targetSelector - The live DOM selector to morph into.
   */
  #morphSwapContent(fragment, sourceSelector, targetSelector) {
    const source = fragment.querySelector(sourceSelector);
    const target = this.querySelector(targetSelector);
    if (source && target) {
      morph(target, source);
    }
  }

  /**
   * Swaps the title content from the template into the live DOM.
   *
   * A card can render more than one title block and each block renders a different snippet, so
   * the template ships one fragment per kind and every wrapper is morphed with the fragment
   * matching its data-swap-title-kind. The hrefs are updated by #swapUrls.
   *
   * @param {DocumentFragment} fragment - The template content.
   */
  #swapTitleContent(fragment) {
    for (const titleWrapper of this.querySelectorAll('[data-swap-title-kind]')) {
      const kind = titleWrapper.getAttribute('data-swap-title-kind') ?? 'title';
      const source = fragment.querySelector(`[data-swap="title-inner"][data-swap-title-kind="${kind}"]`);
      if (!source) continue;

      const textBlock = titleWrapper.querySelector('.text-block, [class*="text"]');
      if (textBlock) {
        morph(textBlock, source);
      }
    }
  }

  /**
   * Swaps the accessible name of the card link from the template into the live DOM.
   *
   * The card link is the single focusable link of the card, so its accessible name must follow
   * the swapped product. The template ships a [data-swap="link-label"] fragment rendered by
   * snippets/product-card-link-label.liquid.
   *
   * @param {DocumentFragment} fragment - The template content.
   */
  #swapLinkLabel(fragment) {
    const source = fragment.querySelector('[data-swap="link-label"]');
    const target = this.querySelector('[data-swap="link-label"]');
    if (source && target) {
      target.textContent = source.textContent;
    }
  }

  /**
   * Updates all URL references from the template.
   * @param {DocumentFragment} fragment - The template content.
   */
  #swapUrls(fragment) {
    const urlData = fragment.querySelector('[data-swap="url"]');
    const newUrl = urlData?.getAttribute('value');
    if (!newUrl) return;

    const { productCardLink } = this.refs;

    if (productCardLink instanceof HTMLAnchorElement) {
      productCardLink.href = newUrl;
    }
  }

  /**
   * Updates quick-add metadata (title, availability) from the template.
   * @param {DocumentFragment} fragment - The template content.
   */
  #swapQuickAddMeta(fragment) {
    const quickAdd = this.refs.quickAdd;
    if (!quickAdd) return;

    const titleData = fragment.querySelector('[data-swap="product-title"]');
    const availableData = fragment.querySelector('[data-swap="available"]');

    if (titleData) {
      quickAdd.setAttribute('data-product-title', titleData.getAttribute('value') || '');
    }

    const isAvailable = availableData?.getAttribute('value') === 'true';
    const addButton = quickAdd.querySelector('.quick-add__button--add');
    const chooseButton = quickAdd.querySelector('.quick-add__button--choose');

    if (addButton instanceof HTMLButtonElement) addButton.disabled = !isAvailable;
    if (chooseButton instanceof HTMLButtonElement) chooseButton.disabled = !isAvailable;
  }

  /**
   * Swaps the slideshow media to show the images of the newly selected product.
   *
   * The swap templates ship fully rendered `slideshow-slide` markup (see
   * snippets/product-card-swap-media.liquid) rather than image data, so the slide elements are
   * replaced with that markup. Doing so preserves the responsive srcset/sizes computed at render
   * time and guarantees that only the images a card can actually display (default + hover) are
   * requested, instead of every image of the swapped product at its original resolution.
   *
   * @param {DocumentFragment} fragment - The template content.
   */
  async #swapMedia(fragment) {
    const { slideshow } = this.refs;
    if (!slideshow) return;

    const sourceSlides = fragment.querySelectorAll('[data-swap="slides"] > slideshow-slide');

    // The swapped product has no media: keep the images that are currently displayed.
    if (sourceSlides.length === 0) return;

    const scroller = /** @type {HTMLElement | null} */ (slideshow.querySelector('slideshow-slides'));
    if (!scroller) return;

    // Replace the slides instead of mutating them in place, so the DOM does not accumulate
    // slides across swaps and hidden slides cannot desync the slideshow index.
    for (const slide of scroller.querySelectorAll(':scope > slideshow-slide')) {
      slide.remove();
    }

    for (const slide of sourceSlides) {
      scroller.appendChild(slide.cloneNode(true));
    }

    // Each slide declares a view-timeline name that must be listed in the slideshow's
    // timeline-scope. The original scope is derived from the original media count, so the names
    // of the swapped slides have to be added to it.
    const timelineNames = new Set(
      (slideshow.style.getPropertyValue('--slideshow-timeline') || '')
        .split(',')
        .map((name) => name.trim())
        .filter(Boolean),
    );

    for (const slide of scroller.querySelectorAll(':scope > slideshow-slide')) {
      const timelineName = /** @type {HTMLElement} */ (slide).style.getPropertyValue('--slideshow-timeline');
      if (timelineName) timelineNames.add(timelineName);
    }

    slideshow.style.setProperty('--slideshow-timeline', [...timelineNames].join(', '));

    // Component refs are refreshed by a MutationObserver, so let it settle before driving the
    // slideshow with the new slides.
    await yieldToMainThread();

    slideshow.select(0, undefined, { animate: false });
  }

  /**
   * Keeps the card gallery metadata in sync with the swapped product.
   *
   * A swapped card only renders the new product's default and hover image, so the carousel
   * affordances no longer apply. When the block adapts the ratio to the media, the gallery ratio
   * is updated as well.
   *
   * @param {DocumentFragment} fragment - The template content.
   */
  #syncGalleryShell(fragment) {
    const { cardGallery } = this.refs;
    if (!(cardGallery instanceof HTMLElement)) return;

    const productId = fragment.querySelector('[data-swap="product-id"]')?.getAttribute('value');
    if (productId) cardGallery.setAttribute('data-product-id', productId);

    // Only the default and hover image remain, so the carousel controls no longer apply.
    // data-generic-media-size mirrors the attribute rendered by card-gallery.liquid.
    const slideshow = /** @type {HTMLElement | null} */ (cardGallery.querySelector('slideshow-component'));
    slideshow?.setAttribute('data-generic-media-size', '0');
    cardGallery.querySelector('slideshow-arrows')?.remove();

    if (cardGallery.getAttribute('data-image-ratio') === 'adapt') {
      const ratio = fragment.querySelector('[data-swap="aspect-ratio"]')?.getAttribute('value');
      if (ratio) cardGallery.style.setProperty('--gallery-aspect-ratio', ratio);
    }
  }

  /**
   * Updates the selected state on variant cards.
   *
   * The cards are exposed as toggle buttons, so the visual selection is mirrored on
   * `aria-pressed` to keep assistive technology in sync with the swapped product image.
   *
   * @param {HTMLElement} activeCard - The newly selected variant card.
   */
  #updateVariantSelection(activeCard) {
    const allVariants = this.querySelectorAll('.product-variant');
    allVariants.forEach((card) => {
      const isActive = card === activeCard;
      card.classList.toggle('product-variant--selected', isActive);
      card.setAttribute('aria-pressed', String(isActive));
    });
  }

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 * @typedef {import('@theme/variant-picker').VariantPickerRefs & {overflowList: HTMLElement}} SwatchesRefs
 */

/**
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  connectedCallback() {
    super.connectedCallback();

    // Cache the parent product card
    this.parentProductCard = this.closest('product-card');

    // Listen for variant updates to apply pending URL changes
    this.addEventListener(StandardEvents.productSelect, this.#handleCardProductSelect.bind(this));
  }

  /**
   * Updates the card URL when a variant is selected.
   * @param {ProductSelectEvent} event
   */
  #handleCardProductSelect(event) {
    // Handle URL update via promise resolution
    event.promise
      .then(() => {
        if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
          const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
          currentUrl.searchParams.set('variant', this.pendingVariantId);
          this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
          this.pendingVariantId = null;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-card] Event promise rejected:', error);
      });
  }

  /**
   * Override the variantChanged method to handle unavailable swatches with available alternatives.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    // Check if this is a swatch input
    const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes('-swatch');
    const clickedSwatch = event.target;
    const availableCount = parseInt(clickedSwatch.dataset.availableCount || '0');
    const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

    // For swatch inputs, check if we need special handling
    if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
      // If this is an unavailable variant but there are available alternatives
      // Prevent the default handling
      event.stopPropagation();

      // Update the selected option visually
      this.updateSelectedOption(clickedSwatch);

      // Build request URL with the first available variant
      const productUrl = this.dataset.productUrl?.split('?')[0];

      if (!productUrl) return;

      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', firstAvailableVariantId);
      url.searchParams.set('section_id', 'section-rendering-product-card');

      const requestUrl = url.href;

      // Store the variant ID we want to apply to the URL
      this.pendingVariantId = firstAvailableVariantId;

      // Use parent's fetch method
      this.fetchUpdatedSection(requestUrl);
      return;
    }

    // For all other cases, use the default behavior
    super.variantChanged(event);
  }

  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
